import Fuse from 'fuse.js';
import React, { useEffect, useMemo, useState } from 'react';

import {
    CardCheck,
    CardCheckbox,
    CheckboxGroup,
    CheckboxGroupField,
    EmptyState,
    Heading,
    IsolateField,
    TextInput,
} from '@gitbook/spine-react';
import { assert } from '@gitbook/util-ts';

import { capitalizeFirstLetter } from '../../../../utils';
import { OpenAPISelectionList } from './OpenAPISelectionList';
import type { Scroller } from './types';

interface SelectableItem {
    value: string;
}

/**
 * Filterable list of OpenAPI selectable items.
 */
export function OpenAPISelectableItems<T extends SelectableItem>(props: {
    items: T[];
    type: 'operation' | 'schema' | 'webhook';
    onToggle: (value: string) => void;
    renderCardContent: (props: { item: T; children: React.ReactNode }) => React.ReactElement;
    searchKeys: Fuse.FuseOptionKeyObject[];
    specTitle: string;
}) {
    const { items, onToggle, renderCardContent, searchKeys, type, specTitle } = props;

    const inputRef = React.useRef<HTMLInputElement | null>(null);
    const checkboxGroupRef = React.useRef<HTMLDivElement | null>(null);
    const scrollerRef = React.useRef<Scroller | null>(null);

    // Represent the virtually selected (focused) item in the list
    const [selected, setSelected] = React.useState<T | null>(null);

    const [searchQuery, setSearchQuery] = useState('');

    const fuse = useMemo(() => {
        return new Fuse(items, {
            threshold: 0.6,
            distance: 100,
            minMatchCharLength: 1,
            keys: searchKeys,
        });
    }, [items, searchKeys]);

    const filteredItems = useMemo(() => {
        if (searchQuery) {
            return fuse.search(searchQuery).map(({ item }) => item);
        }
        return items;
    }, [fuse, items, searchQuery]);

    // Reset virtualFocusedIndex when filteredItems changes
    useEffect(() => {
        if (!selected || !filteredItems.includes(selected)) {
            setSelected(filteredItems[0] ?? null);
        }
    }, [selected, filteredItems]);

    return (
        <CheckboxGroupField
            className="flex-1"
            name={type === 'operation' ? 'operations' : type === 'schema' ? 'schemas' : 'webhooks'}
        >
            <Heading>{`
                ${capitalizeFirstLetter(type)}s from ${specTitle}
            `}</Heading>
            <IsolateField>
                <TextInput
                    ref={inputRef}
                    placeholder={`Find ${type}...`}
                    value={searchQuery}
                    onChange={(e) => {
                        setSelected(filteredItems[0] ?? null);
                        setSearchQuery(e.target.value);
                        scrollerRef.current?.scrollToIndex(0);
                    }}
                    className="mb-2"
                    autoFocus
                    onKeyDown={(event) => {
                        const group = checkboxGroupRef.current;
                        if (!group) {
                            return;
                        }

                        if (!selected) {
                            return;
                        }

                        if (event.key === 'Enter') {
                            event.preventDefault();
                            onToggle(selected.value);
                            return;
                        }

                        const scroller = scrollerRef.current;
                        if (!scroller) {
                            return;
                        }

                        const index = filteredItems.indexOf(selected);
                        if (index === -1) {
                            return;
                        }

                        const nextIndex = (() => {
                            if (event.key === 'ArrowDown') {
                                return (index + 1) % filteredItems.length;
                            }
                            if (event.key === 'ArrowUp') {
                                return (index - 1 + filteredItems.length) % filteredItems.length;
                            }
                            return null;
                        })();

                        if (nextIndex === null) {
                            return;
                        }

                        const nextItem = filteredItems[nextIndex];
                        event.preventDefault();
                        scroller.scrollToIndex(nextIndex, { align: 'center' });
                        setSelected(nextItem);
                    }}
                />
            </IsolateField>
            {filteredItems.length > 0 ? (
                <CheckboxGroup
                    ref={checkboxGroupRef}
                    aria-label={`Available ${type}s`}
                    orientation="vertical"
                    className="relative flex-1"
                    onChange={() => {
                        const input = inputRef.current;
                        assert(input);
                        if (input) {
                            if (document.activeElement !== input) {
                                input.focus();
                            }
                        }
                    }}
                >
                    <OpenAPISelectionList
                        className="-mx-4 -mt-1 px-4 pt-1"
                        scrollerRef={scrollerRef}
                        items={filteredItems}
                    >
                        {({ item, index, ref }) => {
                            const isSelected = selected === item;
                            return (
                                <CardCheckbox
                                    key={index}
                                    ref={ref}
                                    data-index={index}
                                    data-value={item.value}
                                    value={item.value}
                                    className={isSelected ? 'ring-2 ring-focus' : undefined}
                                    onChange={() => setSelected(item)}
                                >
                                    {renderCardContent({
                                        item,
                                        children: <CardCheck />,
                                    })}
                                </CardCheckbox>
                            );
                        }}
                    </OpenAPISelectionList>
                </CheckboxGroup>
            ) : (
                <EmptyState>
                    <Heading>{`No ${type}s found matching your search`}</Heading>
                </EmptyState>
            )}
        </CheckboxGroupField>
    );
}
